import type { AssetId } from './assets';
import type { ChainAddressId } from './chainAssets';
import type { ChainAsset } from './groups';
/**
 * Support levels are:
 * - full: Supported for all actions 3
 * - send: Supported for recovery actions 2
 * - view: Supported for viewing in wallets, but not for any actions 1
 * - none: Not supported for any actions 0
 *
 * This ordering allows us to use numbers to compare support levels
 */
export const SUPPORT_LEVELS = ['full', 'send', 'view', 'none'] as const;

export const DEFAULT_SUPPORT_LEVEL = 'full';
export const DEFAULT_POSITION_SUPPORT_LEVEL = 'send';
export const SUPPORT_LEVEL_INDEXES = {
  full: 3,
  send: 2,
  view: 1,
  none: 0,
} as const;

export type SupportLevel = (typeof SUPPORT_LEVELS)[number];

export type SupportLevelsByChainAddressId = Record<
  ChainAddressId,
  SupportLevel
>;

export type AssetConfig = {
  officialChainAssets: (ChainAsset & { assetId: AssetId })[];
  chainAddressSupportLevels: SupportLevelsByChainAddressId;
};

/**
 * Checks that a ChainAssets support level is at least as high as the desired support level
 * @param chainAddressSupportLevels The ChainAddress support levels
 * @param supportLevel The desired support level
 * @param chainAddressId The ChainAddressId to check
 * @returns boolean
 */
export const checkChainAddressSupportLevel = ({
  chainAddressSupportLevels,
  supportLevel,
  chainAddressId,
}: {
  chainAddressSupportLevels: SupportLevelsByChainAddressId;
  supportLevel: SupportLevel;
  chainAddressId: ChainAddressId;
}): boolean => {
  const chainAddressSupportLevel =
    chainAddressSupportLevels[chainAddressId] ?? DEFAULT_SUPPORT_LEVEL;
  return (
    SUPPORT_LEVEL_INDEXES[chainAddressSupportLevel] >=
    SUPPORT_LEVEL_INDEXES[supportLevel]
  );
};
